import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingBag, Tag, LogOut, Plus, Edit2, Trash2,
  X, Eye, TrendingUp, IndianRupee, Users, ClipboardList, Upload, Image as ImageIcon, FileUp, Download, Trash, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PRODUCTS as SEED_PRODUCTS, CATEGORIES as SEED_CATEGORIES, MOCK_ORDERS } from '../data/mock';
import { useToast } from '../hooks/use-toast';
import { fileToDataUrl, getPrimaryImage } from '../lib/imageUtils';
import { uploadFile, fileUrl } from '../lib/api';
import { computePrices, PRICE_TIERS } from '../lib/pricing';
import { notifyCatalogChanged, catalogApi } from '../lib/catalog';

const STORAGE = {
  products: 'es_admin_products_v5',
  categories: 'es_admin_categories_v4',
  orders: 'es_orders',
  deleted: 'es_admin_deleted_products_v4',
};

const load = (key, seed) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : seed;
  } catch {
    return seed;
  }
};
const save = (key, val) => localStorage.setItem(key, JSON.stringify(val));

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(SEED_CATEGORIES);
  const [deleted, setDeleted] = useState([]);
  const [orders, setOrders] = useState(() => {
    const stored = load(STORAGE.orders, []);
    return stored.length ? stored : MOCK_ORDERS;
  });
  const [catalogLoading, setCatalogLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('es_admin_auth')) navigate('/admin');
  }, [navigate]);

  // Initial load: seed backend if empty, then fetch products/categories/deleted
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await catalogApi.seedIfEmpty();
        const { products: p, categories: c } = await catalogApi.list();
        const del = await catalogApi.listDeleted();
        if (!mounted) return;
        setProducts(p);
        setCategories(c.length ? c : SEED_CATEGORIES);
        setDeleted(del);
      } catch (e) {
        console.error('Catalog load failed', e);
      } finally {
        if (mounted) setCatalogLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const reloadProducts = async () => {
    const { products: p, categories: c } = await catalogApi.list();
    const del = await catalogApi.listDeleted();
    setProducts(p);
    setCategories(c.length ? c : SEED_CATEGORIES);
    setDeleted(del);
    notifyCatalogChanged();
  };

  // Note: products/categories persistence is now via backend API. localStorage no longer used.

  const logout = () => {
    localStorage.removeItem('es_admin_auth');
    navigate('/admin');
  };

  const stats = useMemo(() => {
    const revenue = orders.reduce((s, o) => s + o.total, 0);
    return {
      revenue,
      orders: orders.length,
      products: products.length,
      pending: orders.filter((o) => o.status === 'pending').length,
    };
  }, [orders, products]);

  const menu = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'categories', label: 'Categories', icon: Tag },
    { id: 'orders', label: 'Orders', icon: ShoppingBag },
    { id: 'recyclebin', label: 'Recycle Bin', icon: Trash, badge: deleted.length },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-screen sticky top-0">
          <div className="p-4 border-b">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_seekho-admin/artifacts/2biwyt6k_6305294127389675025.jpg"
                alt="Ecom Seekho"
                className="w-9 h-9 rounded object-cover"
              />
              <div>
                <div className="font-bold text-sm text-gray-900">Ecom Seekho</div>
                <div className="text-[10px] text-gray-500">Admin Panel</div>
              </div>
            </Link>
          </div>
          <nav className="p-3 space-y-1">
            {menu.map((m) => (
              <button
                key={m.id}
                onClick={() => setTab(m.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded text-sm ${tab === m.id ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}
              >
                <m.icon className="w-4 h-4" /> {m.label}
                {m.badge > 0 && (
                  <span className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                    {m.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <div className="p-3 mt-4 border-t">
            <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2 rounded text-sm text-red-600 hover:bg-red-50">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 p-6">
          {tab === 'dashboard' && <Dashboard stats={stats} orders={orders} />}
          {tab === 'products' && <ProductsAdmin products={products} setProducts={setProducts} categories={categories} deleted={deleted} setDeleted={setDeleted} toast={toast} reload={reloadProducts} loading={catalogLoading} />}
          {tab === 'categories' && <CategoriesAdmin categories={categories} setCategories={setCategories} toast={toast} reload={reloadProducts} />}
          {tab === 'orders' && <OrdersAdmin orders={orders} setOrders={setOrders} toast={toast} />}
          {tab === 'recyclebin' && <RecycleBin deleted={deleted} setDeleted={setDeleted} products={products} setProducts={setProducts} categories={categories} toast={toast} reload={reloadProducts} />}
        </main>
      </div>
    </div>
  );
};

const Dashboard = ({ stats, orders }) => (
  <div>
    <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <StatCard label="Total Revenue" value={`₹${stats.revenue.toFixed(0)}`} icon={IndianRupee} color="emerald" />
      <StatCard label="Orders" value={stats.orders} icon={ShoppingBag} color="orange" />
      <StatCard label="Products" value={stats.products} icon={Package} color="emerald" />
      <StatCard label="Pending Orders" value={stats.pending} icon={ClipboardList} color="orange" />
    </div>

    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h2 className="font-semibold text-gray-800 mb-4">Recent Orders</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 border-b">
            <tr>
              <th className="py-2">Order ID</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.slice(0, 8).map((o) => (
              <tr key={o.id} className="border-b last:border-b-0">
                <td className="py-2 font-mono text-xs">{o.id}</td>
                <td>{o.customer}</td>
                <td className="text-gray-500">{o.date}</td>
                <td className="font-semibold">₹{o.total.toFixed(2)}</td>
                <td><StatusBadge status={o.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs text-gray-500 uppercase">{label}</div>
        <div className="text-2xl font-bold text-gray-800 mt-1">{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color === 'emerald' ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-orange-100 text-orange-700',
    shipped: 'bg-blue-100 text-blue-700',
    delivered: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return <span className={`text-[10px] font-semibold px-2 py-1 rounded uppercase ${map[status] || 'bg-gray-100'}`}>{status}</span>;
};

const ProductsAdmin = ({ products, setProducts, categories, deleted, setDeleted, toast, reload, loading }) => {
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [showBulkImport, setShowBulkImport] = useState(false);
  const empty = { id: '', sku: '', name: '', category: categories[0]?.id || '', purchase_price: 0, mrp: 0, price: 0, image: '', images: [], stock: 0, tier1: { qty: 24, price: 0 }, tier2: { qty: 49, price: 0 }, description: '' };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
  const visible = filtered.slice(0, 50);
  const allVisibleSelected = visible.length > 0 && visible.every((p) => selected.has(p.id));

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visible.forEach((p) => next.delete(p.id));
      } else {
        visible.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleSave = async (data) => {
    try {
      if (data.id && products.find((p) => p.id === data.id)) {
        await catalogApi.updateProduct(data.id, data);
        toast({ title: 'Product updated' });
      } else {
        await catalogApi.createProduct(data);
        toast({ title: 'Product added' });
      }
      setEditing(null);
      await reload();
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Move this product to Recycle Bin?')) return;
    try {
      await catalogApi.softDelete(id);
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast({ title: 'Moved to Recycle Bin' });
      await reload();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Move ${selected.size} selected product${selected.size > 1 ? 's' : ''} to Recycle Bin?`)) return;
    try {
      await Promise.all([...selected].map((id) => catalogApi.softDelete(id)));
      toast({ title: `${selected.size} product${selected.size > 1 ? 's' : ''} moved to Recycle Bin` });
      clearSelection();
      await reload();
    } catch (e) {
      toast({ title: 'Bulk delete failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleBulkImport = async (rows) => {
    const now = Date.now();
    const newProducts = rows.map((r, idx) => ({
      id: 'p' + (now + idx),
      sku: 'M' + (now + idx),
      name: r.name,
      category: r.category || categories[0]?.id || 'basket',
      purchase_price: r.purchase_price,
      mrp: r.mrp,
      price: r.price,
      stock: r.stock,
      image: r.image || '',
      images: r.images || (r.image ? [r.image] : []),
      gst: 18,
      tier1: r.tier1 || { qty: 24, price: +(r.price * 0.97).toFixed(2) },
      tier2: r.tier2 || { qty: 49, price: +(r.price * 0.93).toFixed(2) },
      description: `${r.name} — wholesale quality`,
    }));
    try {
      await catalogApi.bulkUpsertProducts(newProducts, false);
      toast({ title: `${newProducts.length} products added` });
      setShowBulkImport(false);
      await reload();
    } catch (e) {
      toast({ title: 'Bulk import failed', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Products ({products.length})</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowBulkImport(true)} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50">
            <FileUp className="w-4 h-4 mr-1" /> Bulk Add
          </Button>
          <Button onClick={() => setEditing({ ...empty })} className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add Product
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" />
        {selected.size > 0 && (
          <div className="flex items-center gap-2 ml-auto bg-red-50 border border-red-200 rounded px-3 py-1.5">
            <span className="text-sm text-red-700 font-medium">{selected.size} selected</span>
            <Button size="sm" onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white h-8">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Move to Recycle Bin
            </Button>
            <button onClick={clearSelection} className="text-xs text-gray-500 hover:text-gray-800 underline">Clear</button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    className="w-4 h-4 accent-emerald-600 cursor-pointer"
                    aria-label="Select all visible"
                  />
                </th>
                <th className="px-3 py-3">Image</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Category</th>
                <th className="px-3 py-3">Price</th>
                <th className="px-3 py-3">Stock</th>
                <th className="px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => (
                <tr key={p.id} className={`border-t hover:bg-gray-50 ${selected.has(p.id) ? 'bg-emerald-50/50' : ''}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleOne(p.id)}
                      className="w-4 h-4 accent-emerald-600 cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-2"><img src={fileUrl(getPrimaryImage(p))} alt={p.name} className="w-12 h-12 object-cover rounded" /></td>
                  <td className="px-3 py-2 max-w-xs">
                    <div className="font-medium text-gray-800 line-clamp-1">{p.name}</div>
                    <div className="text-[10px] text-gray-500">SKU: {p.sku}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{categories.find(c => c.id === p.category)?.name || p.category}</td>
                  <td className="px-3 py-2 font-semibold text-emerald-700">₹{p.price.toFixed(2)}</td>
                  <td className="px-3 py-2">{p.stock}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(p)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-700"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 50 && <div className="p-3 text-center text-xs text-gray-500">Showing first 50 of {filtered.length}</div>}
      </div>

      {editing && <ProductForm product={editing} categories={categories} onSave={handleSave} onClose={() => setEditing(null)} />}
      {showBulkImport && <BulkImportModal categories={categories} onImport={handleBulkImport} onClose={() => setShowBulkImport(false)} />}
    </div>
  );
};

const ProductForm = ({ product, categories, onSave, onClose }) => {
  const [form, setForm] = useState(() => ({
    ...product,
    images: product.images && product.images.length > 0 ? product.images : (product.image ? [product.image] : []),
  }));
  const [uploading, setUploading] = useState(false);
  const handle = (k, v) => setForm({ ...form, [k]: v });

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 4 - form.images.length;
    if (remaining <= 0) return;
    setUploading(true);
    try {
      const toUpload = files.slice(0, remaining);
      const uploaded = await Promise.all(
        toUpload.map(async (f) => {
          // Client-side resize before upload to save bandwidth
          const dataUrl = await fileToDataUrl(f, 1200, 0.85);
          // Convert data URL back to blob for upload
          const blob = await (await fetch(dataUrl)).blob();
          const ext = (f.name.split('.').pop() || 'jpg').toLowerCase();
          const resizedFile = new File([blob], `img.${ext === 'png' ? 'png' : 'jpg'}`, { type: blob.type });
          const res = await uploadFile(resizedFile);
          return res.url; // e.g. /api/files/{id}
        })
      );
      const newImages = [...form.images, ...uploaded].slice(0, 4);
      setForm({ ...form, images: newImages, image: newImages[0] || '' });
    } catch (err) {
      console.error(err);
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    const newImages = form.images.filter((_, i) => i !== idx);
    setForm({ ...form, images: newImages, image: newImages[0] || '' });
  };

  const moveToPrimary = (idx) => {
    if (idx === 0) return;
    const newImages = [form.images[idx], ...form.images.filter((_, i) => i !== idx)];
    setForm({ ...form, images: newImages, image: newImages[0] });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <h2 className="font-bold text-lg">{form.id ? 'Edit' : 'Add'} Product</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => handle('name', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>SKU</Label>
              <Input value={form.sku} onChange={(e) => handle('sku', e.target.value)} />
            </div>
            <div>
              <Label>Category *</Label>
              <select value={form.category} onChange={(e) => handle('category', e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-emerald-800">Purchase Price (₹) — Auto Pricing</Label>
              <span className="text-[10px] text-emerald-700 font-medium">Formula: +15% / +12% / +10%</span>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <Input
                type="number"
                step="0.01"
                value={form.purchase_price || ''}
                onChange={(e) => handle('purchase_price', parseFloat(e.target.value) || 0)}
                placeholder="Enter cost price"
                className="bg-white"
              />
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  const p = computePrices(form.purchase_price);
                  setForm({
                    ...form,
                    price: p.price,
                    tier1: p.tier1,
                    tier2: p.tier2,
                    mrp: form.mrp && form.mrp > p.price ? form.mrp : Math.round(p.price * 1.5 * 100) / 100,
                  });
                }}
                className="bg-emerald-700 hover:bg-emerald-800 text-white h-9"
              >
                Apply Formula
              </Button>
            </div>
            {form.purchase_price > 0 && (
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-white rounded p-2 border border-emerald-200 text-center">
                  <div className="text-[10px] text-gray-500">1–23 pcs (+15%)</div>
                  <div className="font-bold text-emerald-700">₹{(form.purchase_price * 1.15).toFixed(2)}</div>
                </div>
                <div className="bg-white rounded p-2 border border-emerald-200 text-center">
                  <div className="text-[10px] text-gray-500">24–48 pcs (+12%)</div>
                  <div className="font-bold text-emerald-700">₹{(form.purchase_price * 1.12).toFixed(2)}</div>
                </div>
                <div className="bg-white rounded p-2 border border-emerald-200 text-center">
                  <div className="text-[10px] text-gray-500">49+ pcs (+10%)</div>
                  <div className="font-bold text-emerald-700">₹{(form.purchase_price * 1.10).toFixed(2)}</div>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><Label>MRP</Label><Input type="number" step="0.01" value={form.mrp} onChange={(e) => handle('mrp', parseFloat(e.target.value))} /></div>
            <div><Label>Selling Price</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => handle('price', parseFloat(e.target.value))} /></div>
            <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => handle('stock', parseInt(e.target.value))} /></div>
          </div>

          {/* Product Images (1-4) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Product Images (up to 4)</Label>
              <span className="text-[10px] text-gray-500">{form.images.length}/4 uploaded</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {form.images.map((src, idx) => (
                <div key={idx} className="relative aspect-square border-2 border-gray-200 rounded-md overflow-hidden group">
                  <img src={fileUrl(src)} alt={`preview-${idx}`} className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <span className="absolute top-1 left-1 bg-emerald-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">Primary</span>
                  )}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => moveToPrimary(idx)}
                        className="text-[10px] bg-white text-gray-800 px-2 py-1 rounded hover:bg-emerald-100"
                        title="Set as primary"
                      >
                        Set Primary
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="text-[10px] bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              {form.images.length < 4 && (
                <label className="aspect-square border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-[10px] text-gray-500">Uploading…</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-gray-400" />
                      <span className="text-[10px] text-gray-500 text-center px-1">Click to upload</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleFiles}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
            <p className="text-[10px] text-gray-500 mt-1">The first image is used as the main product image. Hover to change or remove.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tier 1 Qty</Label>
              <Input type="number" value={form.tier1?.qty || 0} onChange={(e) => handle('tier1', { ...form.tier1, qty: parseInt(e.target.value) })} />
            </div>
            <div>
              <Label>Tier 1 Price</Label>
              <Input type="number" step="0.01" value={form.tier1?.price || 0} onChange={(e) => handle('tier1', { ...form.tier1, price: parseFloat(e.target.value) })} />
            </div>
            <div>
              <Label>Tier 2 Qty</Label>
              <Input type="number" value={form.tier2?.qty || 0} onChange={(e) => handle('tier2', { ...form.tier2, qty: parseInt(e.target.value) })} />
            </div>
            <div>
              <Label>Tier 2 Price</Label>
              <Input type="number" step="0.01" value={form.tier2?.price || 0} onChange={(e) => handle('tier2', { ...form.tier2, price: parseFloat(e.target.value) })} />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => handle('description', e.target.value)} />
          </div>
        </div>
        <div className="p-4 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(form)} className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={uploading}>Save</Button>
        </div>
      </div>
    </div>
  );
};

// ---------------- Bulk Image Listing Modal ----------------
const BulkImportModal = ({ categories, onImport, onClose }) => {
  const [rows, setRows] = useState([]); // each row: { imageUrl, name, purchase_price, stock, category }
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        files.map(async (f) => {
          try {
            const dataUrl = await fileToDataUrl(f, 1200, 0.85);
            const blob = await (await fetch(dataUrl)).blob();
            const ext = (f.name.split('.').pop() || 'jpg').toLowerCase();
            const resized = new File([blob], `img.${ext === 'png' ? 'png' : 'jpg'}`, { type: blob.type });
            const res = await uploadFile(resized);
            return {
              imageUrl: res.url,
              name: f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
              purchase_price: '',
              stock: 50,
              category: categories[0]?.id || '',
            };
          } catch (err) {
            console.error('Upload failed for', f.name, err);
            return null;
          }
        })
      );
      setRows((prev) => [...prev, ...uploaded.filter(Boolean)]);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const updateRow = (idx, key, val) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const validRows = rows.filter((r) => r.name.trim() && parseFloat(r.purchase_price) > 0 && r.category);

  const handleImport = () => {
    const toImport = validRows.map((r) => {
      const purchase = parseFloat(r.purchase_price);
      const p = computePrices(purchase);
      return {
        name: r.name.trim(),
        category: r.category,
        purchase_price: purchase,
        price: p.price,
        tier1: p.tier1,
        tier2: p.tier2,
        mrp: Math.round(p.price * 1.5 * 100) / 100,
        stock: parseInt(r.stock) || 50,
        image: r.imageUrl,
        images: [r.imageUrl],
      };
    });
    onImport(toImport);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b bg-white">
          <div>
            <h2 className="font-bold text-lg">Bulk Add Products</h2>
            <p className="text-xs text-gray-500">Upload images, then enter <strong>purchase price</strong>, name & category. Selling prices are auto-calculated: +15% (1–23), +12% (24–48), +10% (49+).</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-emerald-400 rounded bg-white hover:bg-emerald-50 cursor-pointer text-sm text-emerald-700 font-medium">
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                Uploading…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> Select Product Images
              </>
            )}
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleFiles}
              disabled={uploading}
            />
          </label>
          {rows.length > 0 && (
            <span className="ml-auto text-xs text-gray-600">
              {rows.length} image{rows.length > 1 ? 's' : ''} · {validRows.length} ready
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {rows.length === 0 ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <ImageIcon className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500 font-medium">No products yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Select Product Images" above to upload multiple images at once.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row, idx) => {
                const purchase = parseFloat(row.purchase_price) || 0;
                const isValid = row.name.trim() && purchase > 0 && row.category;
                return (
                  <div
                    key={idx}
                    className={`bg-white rounded-lg border-2 p-3 ${isValid ? 'border-emerald-200' : 'border-gray-200'}`}
                  >
                    <div className="flex flex-col md:flex-row gap-3 md:items-center">
                      <img
                        src={fileUrl(row.imageUrl)}
                        alt="preview"
                        className="w-20 h-20 md:w-24 md:h-24 object-cover rounded border border-gray-100 flex-shrink-0"
                      />
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 min-w-0">
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Product Name *</label>
                          <Input
                            value={row.name}
                            onChange={(e) => updateRow(idx, 'name', e.target.value)}
                            placeholder="e.g. Storage Basket"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Purchase Price (₹) *</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={row.purchase_price}
                            onChange={(e) => updateRow(idx, 'purchase_price', e.target.value)}
                            placeholder="Cost price"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Stock</label>
                          <Input
                            type="number"
                            value={row.stock}
                            onChange={(e) => updateRow(idx, 'stock', e.target.value)}
                            className="h-9 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Category *</label>
                          <select
                            value={row.category}
                            onChange={(e) => updateRow(idx, 'category', e.target.value)}
                            className="w-full h-9 border-2 border-emerald-300 focus:border-emerald-500 rounded px-2 text-sm bg-emerald-50 font-medium text-emerald-800"
                          >
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={() => removeRow(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    {purchase > 0 && (
                      <div className="mt-2 pt-2 border-t border-dashed border-emerald-200 grid grid-cols-3 gap-1 text-[10px]">
                        <div className="bg-emerald-50 rounded px-2 py-1 text-center">
                          <span className="text-gray-500">1–23:</span> <span className="font-bold text-emerald-700">₹{(purchase * 1.15).toFixed(2)}</span>
                        </div>
                        <div className="bg-emerald-50 rounded px-2 py-1 text-center">
                          <span className="text-gray-500">24–48:</span> <span className="font-bold text-emerald-700">₹{(purchase * 1.12).toFixed(2)}</span>
                        </div>
                        <div className="bg-emerald-50 rounded px-2 py-1 text-center">
                          <span className="text-gray-500">49+:</span> <span className="font-bold text-emerald-700">₹{(purchase * 1.10).toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-between items-center bg-white">
          <span className="text-xs text-gray-500">
            {rows.length - validRows.length > 0 && (
              <>{rows.length - validRows.length} item(s) missing name, purchase price, or category</>
            )}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleImport}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
              disabled={validRows.length === 0 || uploading}
            >
              <Plus className="w-4 h-4 mr-1" /> Add {validRows.length > 0 ? `${validRows.length} Products` : 'Products'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------- Recycle Bin ----------------
const RecycleBin = ({ deleted, setDeleted, products, setProducts, categories, toast, reload }) => {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');

  const filtered = deleted.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
  const allSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id));

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const toggleAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allSelected) filtered.forEach((p) => n.delete(p.id));
    else filtered.forEach((p) => n.add(p.id));
    return n;
  });

  const clearSel = () => setSelected(new Set());

  const restore = async (id) => {
    try {
      await catalogApi.restore(id);
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast({ title: 'Product restored' });
      await reload();
    } catch (e) {
      toast({ title: 'Restore failed', variant: 'destructive' });
    }
  };

  const permanentDelete = async (id) => {
    if (!window.confirm('Permanently delete this product? This CANNOT be undone.')) return;
    try {
      await catalogApi.permanentDelete(id);
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      toast({ title: 'Permanently deleted' });
      await reload();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    try {
      await Promise.all([...selected].map((id) => catalogApi.restore(id)));
      toast({ title: `${selected.size} product${selected.size > 1 ? 's' : ''} restored` });
      clearSel();
      await reload();
    } catch (e) {
      toast({ title: 'Bulk restore failed', variant: 'destructive' });
    }
  };

  const bulkPermanentDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} product${selected.size > 1 ? 's' : ''}? This CANNOT be undone.`)) return;
    try {
      await Promise.all([...selected].map((id) => catalogApi.permanentDelete(id)));
      toast({ title: `${selected.size} permanently deleted` });
      clearSel();
      await reload();
    } catch (e) {
      toast({ title: 'Bulk delete failed', variant: 'destructive' });
    }
  };

  const emptyBin = async () => {
    if (deleted.length === 0) return;
    if (!window.confirm(`Empty the Recycle Bin? All ${deleted.length} product${deleted.length > 1 ? 's' : ''} will be permanently deleted.`)) return;
    try {
      await Promise.all(deleted.map((p) => catalogApi.permanentDelete(p.id)));
      clearSel();
      toast({ title: 'Recycle Bin emptied' });
      await reload();
    } catch (e) {
      toast({ title: 'Empty failed', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Trash className="w-6 h-6 text-red-500" />
            Recycle Bin ({deleted.length})
          </h1>
          <p className="text-xs text-gray-500 mt-1">Deleted products stay here until you restore or permanently delete them.</p>
        </div>
        {deleted.length > 0 && (
          <Button onClick={emptyBin} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50">
            <Trash className="w-4 h-4 mr-1" /> Empty Bin
          </Button>
        )}
      </div>

      {deleted.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Trash className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">Recycle Bin is empty</p>
          <p className="text-xs text-gray-400 mt-1">Products you delete will appear here.</p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Input placeholder="Search deleted products..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" />
            {selected.size > 0 && (
              <div className="flex items-center gap-2 ml-auto bg-orange-50 border border-orange-200 rounded px-3 py-1.5">
                <span className="text-sm text-orange-800 font-medium">{selected.size} selected</span>
                <Button size="sm" onClick={bulkRestore} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                </Button>
                <Button size="sm" onClick={bulkPermanentDelete} className="bg-red-600 hover:bg-red-700 text-white h-8">
                  <Trash className="w-3.5 h-3.5 mr-1" /> Delete Permanently
                </Button>
                <button onClick={clearSel} className="text-xs text-gray-500 hover:text-gray-800 underline">Clear</button>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
                  <tr>
                    <th className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="w-4 h-4 accent-emerald-600 cursor-pointer"
                      />
                    </th>
                    <th className="px-3 py-3">Image</th>
                    <th className="px-3 py-3">Name</th>
                    <th className="px-3 py-3">Category</th>
                    <th className="px-3 py-3">Price</th>
                    <th className="px-3 py-3">Deleted On</th>
                    <th className="px-3 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 100).map((p) => (
                    <tr key={p.id} className={`border-t hover:bg-gray-50 ${selected.has(p.id) ? 'bg-orange-50/40' : ''}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(p.id)}
                          onChange={() => toggleOne(p.id)}
                          className="w-4 h-4 accent-emerald-600 cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2"><img src={fileUrl(getPrimaryImage(p))} alt={p.name} className="w-12 h-12 object-cover rounded opacity-70" /></td>
                      <td className="px-3 py-2 max-w-xs">
                        <div className="font-medium text-gray-700 line-clamp-1">{p.name}</div>
                        <div className="text-[10px] text-gray-500">SKU: {p.sku}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{categories.find((c) => c.id === p.category)?.name || p.category}</td>
                      <td className="px-3 py-2 font-semibold text-gray-700">₹{Number(p.price).toFixed(2)}</td>
                      <td className="px-3 py-2 text-xs text-gray-500">{p.deleted_at ? new Date(p.deleted_at).toLocaleString() : (p.deletedAt ? new Date(p.deletedAt).toLocaleString() : '—')}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button onClick={() => restore(p.id)} title="Restore" className="p-1.5 hover:bg-emerald-50 rounded text-emerald-700">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => permanentDelete(p.id)} title="Delete permanently" className="p-1.5 hover:bg-red-50 rounded text-red-600">
                            <Trash className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length > 100 && <div className="p-3 text-center text-xs text-gray-500">Showing first 100 of {filtered.length}</div>}
          </div>
        </>
      )}
    </div>
  );
};

const CategoriesAdmin = ({ categories, setCategories, toast, reload }) => {
  const [name, setName] = useState('');

  const addCat = async () => {
    if (!name.trim()) return;
    const id = name.toLowerCase().replace(/\s+/g, '-');
    try {
      await catalogApi.createCategory({ id, name: name.trim(), slug: id });
      setName('');
      toast({ title: 'Category added' });
      await reload();
    } catch (e) {
      toast({ title: 'Add failed', description: e.message, variant: 'destructive' });
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await catalogApi.deleteCategory(id);
      await reload();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Categories ({categories.length})</h1>
      <div className="bg-white rounded-lg border border-gray-200 p-5 max-w-2xl">
        <div className="flex gap-2 mb-4">
          <Input placeholder="New category name..." value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCat()} />
          <Button onClick={addCat} className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        <ul className="divide-y">
          {categories.map((c) => (
            <li key={c.id} className="py-2 flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-800">{c.name}</div>
                <div className="text-[10px] text-gray-500">/{c.slug}</div>
              </div>
              <button onClick={() => del(c.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const OrdersAdmin = ({ orders, setOrders, toast }) => {
  const [viewOrder, setViewOrder] = useState(null);

  const updateStatus = (id, status) => {
    const next = orders.map((o) => (o.id === id ? { ...o, status } : o));
    setOrders(next);
    save(STORAGE.orders, next);
    toast({ title: `Order ${id} → ${status}` });
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Orders ({orders.length})</h1>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
              <tr>
                <th className="px-4 py-3">Order ID</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Items</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{o.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{o.customer}</div>
                    <div className="text-[10px] text-gray-500">{o.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{o.date}</td>
                  <td className="px-4 py-3">{o.items.length}</td>
                  <td className="px-4 py-3 font-semibold">₹{o.total.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <select value={o.status} onChange={(e) => updateStatus(o.id, e.target.value)} className="text-xs border rounded px-2 py-1">
                      <option value="pending">Pending</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setViewOrder(o)} className="p-1.5 hover:bg-emerald-50 rounded text-emerald-700">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {viewOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewOrder(null)}>
          <div className="bg-white rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Order {viewOrder.id}</h3>
              <button onClick={() => setViewOrder(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">Customer:</span> <span className="font-medium">{viewOrder.customer}</span></div>
              <div><span className="text-gray-500">Phone:</span> {viewOrder.phone}</div>
              <div><span className="text-gray-500">Address:</span> {viewOrder.address}</div>
              <div><span className="text-gray-500">Date:</span> {viewOrder.date}</div>
              <div><span className="text-gray-500">Status:</span> <StatusBadge status={viewOrder.status} /></div>
              <div className="border-t pt-3 mt-3">
                <h4 className="font-semibold mb-2">Items</h4>
                <ul className="space-y-1">
                  {viewOrder.items.map((i, idx) => (
                    <li key={idx} className="flex justify-between text-xs">
                      <span>{i.name} × {i.qty}</span>
                      <span className="font-medium">₹{(i.price * i.qty).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="border-t pt-3 flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-emerald-700">₹{viewOrder.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
